import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserRoles } from '@shared/services/apiService/apiService';
import { emailRules, passwordRules } from '@shared/constants/validation.constants';
import CustomOutlinedInput from '@shared/components/forms/theme-elements/CustomOutlinedInput';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import { useCreateUser } from '../../hooks/useCreateUser';

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess: () => void;
}

export const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onClose,
  companyId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { control, formState: { errors, isSubmitting }, onSubmit, handleClose, roleOptions } =
    useCreateUser({ companyId, onClose, onSuccess });
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}>
        <Typography variant="h6" component="h2" mb={1}>
          {t('superAdminMainPage.companies.editCompany.createUser.title')}
        </Typography>

        <Controller
          name="firstName"
          control={control}
          rules={{ required: `${t('superAdminMainPage.companies.editCompany.editUser.firstName')} is required` }}
          render={({ field }) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              label={t('superAdminMainPage.companies.editCompany.editUser.firstName')}
              error={Boolean(errors.firstName)}
              helperText={errors.firstName?.message}
            />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          rules={{ required: `${t('superAdminMainPage.companies.editCompany.editUser.lastName')} is required` }}
          render={({ field }) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              label={t('superAdminMainPage.companies.editCompany.editUser.lastName')}
              error={Boolean(errors.lastName)}
              helperText={errors.lastName?.message}
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          rules={emailRules}
          render={({ field }) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              label={t('superAdminMainPage.companies.userTableHeaders.email')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          rules={passwordRules}
          render={({ field }) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label={t('superAdminMainPage.companies.editCompany.editUser.password')}
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t('common.togglePasswordVisibility')}
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          name="role"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FormControl margin="normal" fullWidth>
              <InputLabel id="create-user-role-label">{t('adminMainPage.agents.role')}</InputLabel>
              <CustomSelect
                {...field}
                labelId="create-user-role-label"
                id="create-user-role"
                label={t('adminMainPage.agents.role')}
                onChange={(event: SelectChangeEvent) => field.onChange(event.target.value as UserRoles)}
                input={<CustomOutlinedInput label={t('adminMainPage.agents.role')} />}
                renderValue={(value: string) => <>{value}</>}>
                {roleOptions.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          )}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
            {t('superAdminMainPage.companies.editCompany.createUser.submitButton')}
          </Button>
          <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
            {t('superAdminMainPage.companies.operation.cancel')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
